/**
 * What an item costs to get in, read out of the price composite's parts, and the band that puts it in.
 *
 * ONE DEFINITION FOR TWO READERS. The Climate Week app's Events list filters on a Free / Under $25 /
 * Over $25 chip it computes from these parts, and the space chat's model could not: it saw
 * `Price Amount — number` and `Access — one of: open, invite, …` and, asked for free events, filtered
 * on Access being open, because no field said "free". So the same derivation runs on the server
 * (api/_resultSetRules.js, `derivedAnswers`) and gives the model a `Price Band` to filter on that means
 * exactly what the chip means. It lives here, in the shared utils, so neither side can drift from the other.
 *
 * The parts are those of the canonical `price` composite (migration 20260926170000): the amount, the
 * verbatim label, and the older single `Price` text, read by display name or by key.
 */

/**
 * What a price label costs to get in, in dollars: a range's low end, 0 for free, null when it names no
 * figure ("Pay what you can", "Ticket availability will be shared").
 *
 * **A label that OPENS with free is free.** "Free, with a suggested $50 donation" names a figure, and
 * reading the figure first — the ingest's order — files a free event under Over $25.
 */
export function priceFromText(label) {
  const s = String(label ?? '').replace(/[–—]/g, '-').trim();
  if (!s) return null;
  if (/^(free|no fee|no charge|complimentary)\b/i.test(s)) return 0;
  const figures = [...s.matchAll(/\$\s*(\d[\d,]*(?:\.\d+)?)/g)].map((m) => Number(m[1].replace(/,/g, ''))).filter(Number.isFinite);
  if (figures.length) return Math.min(...figures);
  if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
  if (/\b(free|no fee|no charge|complimentary)\b/i.test(s)) return 0;
  return null;
}

/** The lowest price to get in: the stated amount when there is one, else what the label says. */
export function priceAmountOf(cf = {}) {
  const raw = cf['Price Amount'] ?? cf.price_amount;
  const n = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw.trim() ? Number(raw) : NaN);
  if (Number.isFinite(n) && n >= 0) return n;
  return priceFromText(cf['Price Label'] ?? cf.price_label ?? cf.Price ?? cf.price);
}

/** Which band an amount falls in; no amount is no band, and shows only when no band is chosen. */
export function priceBandOf(amount) {
  if (amount == null || !Number.isFinite(amount)) return null;
  if (amount === 0) return 'free';
  return amount <= 25 ? 'under25' : 'over25';
}

/** The key the derived band is filed under, as a display name like the other jsonb keys beside it. */
export const PRICE_BAND_KEY = 'Price Band';

/** The band for an item's answers, or null when it carries no price at all. */
export function priceBandFor(cf = {}) {
  return priceBandOf(priceAmountOf(cf));
}
